<!-- eslint-disable vue/multi-word-component-names -->
<!-- Layout filenames are fixed by the house structure spec (Default / Blank / Topbar),
     so the multi-word rule cannot apply to this folder.

     One column and no sidebar, like the housekeeping portal and for the same reason:
     this is held in one hand. What differs is where the navigation sits — at the BOTTOM
     on a telephone, where a thumb is, and at the top on anything wider, where a mouse
     is. It is one `<nav>` with two positions rather than two navs: two would be two
     lists to keep in step, and the one that drifted would be the one nobody uses. -->
<template>
    <div class="flex min-h-dvh flex-col bg-muted">
        <Topbar />

        <!--
            `fixed` under `sm` and in the flow above it.

            The safe-area padding is not decoration: on a telephone with a home
            indicator, a bar sitting flush at the bottom puts „Ura“ underneath the
            indicator, and the tap that reaches it is the one iOS takes for itself.
        -->
        <nav
            class="fixed inset-x-0 bottom-0 z-20 flex gap-1 border-t border-default bg-default px-1 pb-[env(safe-area-inset-bottom)] pt-1
                   sm:static sm:z-10 sm:border-b sm:border-t-0 sm:px-4 sm:py-1.5"
        >
            <UButton
                v-for="item in navigation"
                :key="item.name"
                :to="{ name: item.name }"
                :label="item.label"
                :icon="item.icon"
                :color="route.name === item.name ? 'primary' : 'neutral'"
                :variant="route.name === item.name ? 'subtle' : 'ghost'"
                :ui="{ base: 'flex-col gap-0.5 sm:flex-row sm:gap-1.5', label: 'truncate text-[11px] sm:text-sm' }"
                size="lg"
                class="min-w-0 flex-1 justify-center px-0.5 py-2 sm:px-1.5"
            />
        </nav>

        <!-- The bottom padding clears the navigation bar AND whatever iOS floats over
             the page. Without it the last row of a month sits underneath both and simply
             is not there — reported in the housekeeping portal as „ne vidim vseh
             nadstropij“, which was true. -->
        <main
            class="min-w-0 flex-1 p-3 pb-[calc(4.5rem+env(safe-area-inset-bottom))]
                   sm:p-4 sm:pb-[calc(1rem+env(safe-area-inset-bottom))]"
        >
            <slot>
                <RouterView />
            </slot>
        </main>
    </div>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute } from 'vue-router'

    import Topbar from '@/layouts/components/Topbar.vue'

    const { t } = useI18n()
    const route = useRoute()

    /**
     * Five entries, and none of them is gated.
     *
     * Every other portal in this product filters its navigation by a permission, because
     * every other portal has screens some accounts may not open. This one has none:
     * docs/OSEBJE.md §13 makes reading your own record the other side of being IN it, so
     * there is nothing here to hold or to lack. „Moj profil“ is reached from the top bar
     * rather than from here — it is a settings page somebody opens twice a year, and a
     * sixth thumb-width tab would cost the other five their labels.
     *
     * The order is the order of a day and not of a menu: the clock is what the portal is
     * opened for, the week is what is asked about next, and leave and papers are the two
     * things somebody comes back for.
     */
    const navigation = computed(() => [
        { name: 'home',      label: t('nav.home'),      icon: 'i-lucide-house' },
        { name: 'clock',     label: t('nav.clock'),     icon: 'i-lucide-clock' },
        { name: 'rota',      label: t('nav.rota'),      icon: 'i-lucide-calendar-days' },
        { name: 'leave',     label: t('nav.leave'),     icon: 'i-lucide-palmtree' },
        { name: 'documents', label: t('nav.documents'), icon: 'i-lucide-folder' },
    ])
</script>
